import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types & constants ────────────────────────────────────────────────────
type StalkStatus = 'standing' | 'falling' | 'fallen';

interface Stalk {
  id: number;
  height: number;
  leafHue: number;
  leafSat: number;
  swayDur: number;
  swayDelay: number;
  status: StalkStatus;
}

const COMBINE_W = 395;
const HEADER_W = 124;  // header/cutter width (leftmost part of combine)
const STALK_DT = 36;
const STALK_MB = 18;

// Scale per device
const SC_DT = 0.24;   // desktop combine scale (3x smaller)
const SC_MB = 0.09;   // mobile combine scale (3x smaller)
const SC_STK_DT = 0.72; // desktop stalk scale
const SC_STK_MB = 0.42; // mobile stalk scale

// deterministic pseudo-random: no Math.random() to avoid re-init flicker
const dr = (seed: number, range: number, min = 0) => min + ((seed * 2654435761) >>> 0) % range;

const makeStalk = (i: number, n: number): Stalk => ({
  id: i,
  height: 72 + dr(i * 3 + 1, 36),
  leafHue: 112 + dr(i * 7 + 2, 28),
  leafSat: 44 + dr(i * 11, 22),
  swayDur: 2.0 + dr(i * 5 + 3, 18) / 10,
  swayDelay: dr(i * 9 + 4, 22) / 10,
  status: 'standing',
});

// ─── Reel ─────────────────────────────────────────────────────────────────
const Reel = ({ sz }: { sz: number }) => (
  <div className="reel-anim" style={{ width: sz, height: sz, position: 'relative', flexShrink: 0 }}>
    <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid rgba(255,210,60,0.55)` }} />
    {Array.from({ length: 8 }, (_, k) => (
      <div key={k} style={{
        position: 'absolute', width: 2.5, height: sz * 0.43,
        background: 'rgba(255,200,55,0.6)',
        top: '50%', left: '50%',
        marginLeft: -1.25, marginTop: -(sz * 0.43),
        transform: `rotate(${k * 45}deg)`, transformOrigin: '50% 100%',
        borderRadius: 1.5,
      }} />
    ))}
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      width: sz * 0.24, height: sz * 0.24, borderRadius: '50%',
      background: 'radial-gradient(circle at 35% 35%, #fcd34d, #b45309)',
      transform: 'translate(-50%,-50%)', zIndex: 1,
    }} />
  </div>
);

// ─── Spoked wheel ─────────────────────────────────────────────────────────
const Wheel = ({ sz, cls }: { sz: number; cls: string }) => {
  const spokes = sz > 55 ? 8 : 6;
  const border = Math.max(3, Math.round(sz * 0.08));
  return (
    <div className={cls} style={{
      width: sz, height: sz, borderRadius: '50%',
      background: 'radial-gradient(circle at 40% 40%, #2a2a2a, #0d0d0d)',
      border: `${border}px solid #1e1e1e`,
      position: 'relative', overflow: 'visible', flexShrink: 0,
    }}>
      {/* Tread blocks */}
      {Array.from({ length: 14 }, (_, k) => (
        <div key={k} style={{
          position: 'absolute', inset: 2,
          borderRadius: '50%',
          border: `${Math.max(1, Math.round(sz * 0.055))}px solid rgba(60,60,60,0.5)`,
          transform: `rotate(${k * (360 / 14)}deg)`,
          clipPath: 'polygon(48% 0%, 52% 0%, 52% 35%, 48% 35%)',
          background: 'rgba(70,70,70,0.6)',
        }} />
      ))}
      {/* Spokes */}
      {Array.from({ length: spokes }, (_, k) => (
        <div key={k} style={{
          position: 'absolute',
          width: Math.round(sz * 0.09), height: Math.round(sz * 0.37),
          background: 'linear-gradient(to bottom, #fcd34d 0%, #c57a08 100%)',
          top: '50%', left: '50%',
          marginLeft: -Math.round(sz * 0.045),
          marginTop: -Math.round(sz * 0.37),
          transform: `rotate(${k * (360 / spokes)}deg)`,
          transformOrigin: '50% 100%',
          borderRadius: 3,
        }} />
      ))}
      {/* Hub ring */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: sz * 0.30, height: sz * 0.30, borderRadius: '50%',
        background: 'radial-gradient(circle at 33% 33%, #fcd34d, #92400e)',
        transform: 'translate(-50%,-50%)', zIndex: 2,
        border: `${Math.max(1, Math.round(sz * 0.025))}px solid rgba(255,200,50,0.35)`,
      }} />
      {/* Hub bolt */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: sz * 0.12, height: sz * 0.12, borderRadius: '50%',
        background: '#111', transform: 'translate(-50%,-50%)', zIndex: 3,
      }} />
    </div>
  );
};

// ─── Realistic combine harvester ──────────────────────────────────────────
const Combine = ({ sc, moving }: { sc: number; moving: boolean }) => {
  const s = (n: number) => Math.round(n * sc);
  const W = s(COMBINE_W);
  const H = s(200);

  return (
    <div style={{ position: 'relative', width: W, height: H }}>

      {/* ── Unloading auger tube ── */}
      <div style={{
        position: 'absolute', top: s(4), right: s(40),
        width: s(9), height: s(80),
        background: 'linear-gradient(to bottom, #fde68a, #d97706, #92400e)',
        borderRadius: s(5),
        transform: 'rotate(-20deg)', transformOrigin: 'bottom center', zIndex: 7,
      }} />
      <div style={{
        position: 'absolute', top: s(2), right: s(18),
        width: s(22), height: s(13),
        background: 'linear-gradient(135deg, #fcd34d, #d97706)',
        borderRadius: `${s(3)}px ${s(6)}px ${s(6)}px ${s(3)}px`,
        zIndex: 7,
      }} />

      {/* ── Exhaust stack ── */}
      <div style={{
        position: 'absolute', top: s(6), left: s(230),
        width: s(8), height: s(32),
        background: 'linear-gradient(to right, #2d2d2d, #1a1a1a)',
        borderRadius: `${s(4)}px ${s(4)}px ${s(2)}px ${s(2)}px`,
        zIndex: 8,
      }} />
      {/* Smoke */}
      <div style={{ position: 'absolute', left: s(228), top: s(-14) }}>
        <div className="smoke-1 absolute rounded-full" style={{ width: s(14), height: s(14), background: 'rgba(200,200,200,0.3)', left: 0, top: 0 }} />
        <div className="smoke-2 absolute rounded-full" style={{ width: s(10), height: s(10), background: 'rgba(200,200,200,0.2)', left: s(4), top: s(2) }} />
        <div className="smoke-3 absolute rounded-full" style={{ width: s(8),  height: s(8),  background: 'rgba(200,200,200,0.15)', left: s(2), top: s(5) }} />
      </div>

      {/* ── Grain tank ── */}
      <div style={{
        position: 'absolute', top: s(8), right: s(12),
        width: s(122), height: s(104),
        background: 'linear-gradient(155deg, #f5c842 0%, #e8a91a 45%, #c57a08 100%)',
        borderRadius: `${s(12)}px ${s(12)}px ${s(4)}px ${s(4)}px`,
        zIndex: 4,
      }}>
        {/* Tank body markings */}
        {[28, 55, 80].map(y => (
          <div key={y} style={{ position: 'absolute', left: s(10), right: s(10), top: y, height: 1, background: 'rgba(0,0,0,0.1)' }} />
        ))}
        {/* Tank nameplate */}
        <div style={{
          position: 'absolute', top: s(16), left: s(12),
          width: s(36), height: s(14),
          background: 'rgba(0,0,0,0.18)', borderRadius: s(3),
        }} />
        <div style={{
          position: 'absolute', top: s(16), left: s(54),
          width: s(50), height: s(8),
          background: 'rgba(0,0,0,0.1)', borderRadius: s(2),
        }} />
        {/* Grain cap */}
        <div style={{
          position: 'absolute', top: s(-8), left: s(20), right: s(20),
          height: s(16), background: '#b37d0a',
          borderRadius: `${s(8)}px ${s(8)}px 0 0`,
        }} />
      </div>

      {/* ── Cab ── */}
      <div style={{
        position: 'absolute', top: s(14), left: s(180),
        width: s(100), height: s(92),
        background: 'linear-gradient(150deg, #f5c842 0%, #e8a91a 55%, #d97706 100%)',
        borderRadius: `${s(10)}px ${s(10)}px ${s(5)}px ${s(5)}px`,
        zIndex: 5,
      }}>
        {/* Roof */}
        <div style={{
          position: 'absolute', top: s(-8), left: s(-2), right: s(-2),
          height: s(12), background: '#b37d0a',
          borderRadius: `${s(7)}px ${s(7)}px 0 0`,
        }} />
        {/* Front windshield (large) */}
        <div style={{
          position: 'absolute', top: s(10), left: s(6),
          width: s(50), height: s(48),
          background: 'linear-gradient(135deg, rgba(190,230,255,0.88), rgba(130,195,255,0.55))',
          borderRadius: s(6), border: `${s(2)}px solid rgba(255,255,255,0.4)`,
        }}>
          {/* Wiper */}
          <div style={{
            position: 'absolute', bottom: s(8), left: s(5),
            width: s(28), height: s(2),
            background: 'rgba(0,0,0,0.35)', borderRadius: 1,
            transform: 'rotate(-22deg)', transformOrigin: 'left center',
          }} />
          {/* Sun visor strip */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: s(8),
            background: 'rgba(0,0,0,0.15)', borderRadius: `${s(5)}px ${s(5)}px 0 0`,
          }} />
        </div>
        {/* Side window */}
        <div style={{
          position: 'absolute', top: s(10), right: s(7),
          width: s(28), height: s(32),
          background: 'linear-gradient(135deg, rgba(190,230,255,0.65), rgba(130,195,255,0.35))',
          borderRadius: s(4), border: `${s(1.5)}px solid rgba(255,255,255,0.3)`,
        }} />
        {/* Door seam */}
        <div style={{
          position: 'absolute', top: s(60), left: s(18), width: s(1.5), height: s(26),
          background: 'rgba(0,0,0,0.22)',
        }} />
        {/* Entry steps */}
        <div style={{
          position: 'absolute', bottom: s(4), left: s(-10),
          width: s(14), height: s(5),
          background: '#a36308', borderRadius: `0 0 ${s(2)}px ${s(2)}px`,
        }} />
        <div style={{
          position: 'absolute', bottom: s(12), left: s(-10),
          width: s(12), height: s(4),
          background: '#a36308', borderRadius: `0 0 ${s(2)}px ${s(2)}px`, opacity: 0.7,
        }} />
      </div>

      {/* ── Engine hood / front body ── */}
      <div style={{
        position: 'absolute', top: s(80), left: s(140),
        width: s(56), height: s(55),
        background: 'linear-gradient(165deg, #f5c430, #c57a08)',
        borderRadius: `${s(5)}px ${s(4)}px ${s(3)}px ${s(5)}px`,
        zIndex: 4,
      }}>
        {/* Louvers */}
        {[10, 19, 28, 37].map(y => (
          <div key={y} style={{ position: 'absolute', left: s(7), right: s(7), top: y, height: s(4), background: 'rgba(0,0,0,0.18)', borderRadius: s(2) }} />
        ))}
        {/* Front light cluster */}
        <div style={{ position: 'absolute', top: s(8), left: s(4), display: 'flex', flexDirection: 'column', gap: s(4) }}>
          <div style={{
            width: s(9), height: s(6),
            background: moving ? 'radial-gradient(circle, #fff8c0, #fbbf24)' : 'rgba(251,191,36,0.4)',
            borderRadius: s(2),
            boxShadow: moving ? `0 0 ${s(10)}px rgba(254,240,138,0.8)` : 'none',
            transition: 'all 0.3s',
          }} />
          <div style={{
            width: s(9), height: s(6),
            background: moving ? 'radial-gradient(circle, #fca5a5, #ef4444)' : 'rgba(239,68,68,0.3)',
            borderRadius: s(2),
            transition: 'all 0.3s',
          }} />
        </div>
      </div>

      {/* ── Main chassis ── */}
      <div style={{
        position: 'absolute', top: s(102), left: s(110), right: s(10),
        height: s(70),
        background: 'linear-gradient(180deg, #e8a91a 0%, #c57a08 55%, #a36308 100%)',
        borderRadius: `${s(4)}px ${s(6)}px ${s(4)}px ${s(4)}px`,
        zIndex: 3,
      }}>
        {/* Chassis panel lines */}
        <div style={{ position: 'absolute', top: s(18), left: s(10), right: s(10), height: 1, background: 'rgba(0,0,0,0.12)' }} />
        <div style={{ position: 'absolute', top: s(42), left: s(10), right: s(10), height: 1, background: 'rgba(0,0,0,0.09)' }} />
        {/* Rear light bar */}
        <div style={{ position: 'absolute', top: s(8), right: s(14), display: 'flex', gap: s(5) }}>
          {['rgba(254,240,138,0.9)', 'rgba(253,186,116,0.9)'].map((bg, k) => (
            <div key={k} style={{
              width: s(8), height: s(8), borderRadius: '50%', background: bg,
              boxShadow: moving ? `0 0 ${s(8)}px ${bg}` : 'none', transition: 'box-shadow 0.3s',
            }} />
          ))}
        </div>
        {/* Chassis number plate strip */}
        <div style={{
          position: 'absolute', bottom: s(6), left: s(12),
          width: s(40), height: s(10),
          background: 'rgba(0,0,0,0.15)', borderRadius: s(2),
        }} />
      </div>

      {/* ── Feeder house (angled connector) ── */}
      <div style={{
        position: 'absolute', top: s(108), left: s(106),
        width: s(48), height: s(62),
        background: 'linear-gradient(175deg, #d97706, #a36308)',
        clipPath: 'polygon(0% 100%, 100% 100%, 100% 15%, 55% 0%, 0% 5%)',
        zIndex: 5,
      }} />

      {/* ── Header platform ── */}
      <div style={{
        position: 'absolute', top: s(126), left: 0,
        width: s(HEADER_W), height: s(56),
        background: 'linear-gradient(185deg, #d97706 0%, #b45309 60%, #92400e 100%)',
        borderRadius: `${s(5)}px 0 ${s(3)}px ${s(5)}px`,
        zIndex: 6,
      }}>
        {/* Reel housing */}
        <div style={{
          position: 'absolute', top: s(-46), left: s(18),
          width: s(78), height: s(56),
          background: 'rgba(0,0,0,0.22)', borderRadius: `${s(4)}px ${s(4)}px 0 0`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Reel sz={s(50)} />
        </div>
        {/* Divider fingers */}
        {[16, 30, 44, 58, 72, 86, 100, 114].map(x => (
          <div key={x} style={{
            position: 'absolute', top: 0, left: x, bottom: 0,
            width: 1, background: 'rgba(0,0,0,0.18)',
          }} />
        ))}
        {/* Header auger */}
        <div style={{
          position: 'absolute', top: s(5), left: s(8), right: s(8), height: s(17),
          background: 'repeating-linear-gradient(90deg, #fbbf24 0px, #d97706 16px, #fbbf24 16px)',
          borderRadius: s(9), opacity: 0.9,
        }} />
        {/* Cutter bar */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: s(7), background: '#7c3005', borderRadius: `0 0 ${s(3)}px ${s(3)}px`,
        }} />
        {/* Cutter teeth */}
        {Array.from({ length: 12 }, (_, k) => (
          <div key={k} style={{
            position: 'absolute', bottom: s(-11), left: k * (s(HEADER_W) / 12) + s(2),
            width: s(7), height: s(13),
            background: '#6b2d00',
            clipPath: 'polygon(10% 0%, 90% 0%, 50% 100%)',
          }} />
        ))}
      </div>

      {/* ── Rear drive wheels ── */}
      <div style={{ position: 'absolute', bottom: 0, left: s(242), zIndex: 2 }}>
        <Wheel sz={s(82)} cls="wheel-rear-anim" />
      </div>
      {/* Axle bracket */}
      <div style={{
        position: 'absolute', bottom: s(38), left: s(268),
        width: s(20), height: s(8),
        background: '#2a2a2a', borderRadius: s(2), zIndex: 3,
      }} />

      {/* ── Front steerable wheel ── */}
      <div style={{ position: 'absolute', bottom: s(6), left: s(100), zIndex: 2 }}>
        <Wheel sz={s(60)} cls="wheel-front-anim" />
      </div>

      {/* ── Rear support wheel ── */}
      <div style={{ position: 'absolute', bottom: s(4), right: s(8), zIndex: 2 }}>
        <Wheel sz={s(48)} cls="wheel-rear-anim" />
      </div>

      {/* ── Dust cloud (behind/right of combine) ── */}
      <div style={{ position: 'absolute', bottom: s(5), left: s(316) }}>
        <div className="dust-1 absolute rounded-full" style={{ width: s(28), height: s(28), background: 'rgba(150,115,45,0.3)', left: 0, bottom: 0 }} />
        <div className="dust-2 absolute rounded-full" style={{ width: s(18), height: s(18), background: 'rgba(150,115,45,0.2)', left: s(14), bottom: s(4) }} />
        <div className="dust-3 absolute rounded-full" style={{ width: s(22), height: s(22), background: 'rgba(150,115,45,0.15)', left: s(-5), bottom: 0 }} />
      </div>
    </div>
  );
};

// ─── Realistic corn stalk ─────────────────────────────────────────────────
const CornStalk = ({ stalk, sc }: { stalk: Stalk; sc: number }) => {
  const h = Math.round(stalk.height * sc);
  const lc = `hsl(${stalk.leafHue}, ${stalk.leafSat}%, 27%)`;
  const lc2 = `hsl(${stalk.leafHue}, ${stalk.leafSat - 6}%, 21%)`;
  const sw = Math.max(3, Math.round(3.5 * sc));
  const falling = stalk.status !== 'standing';
  const gone = stalk.status === 'fallen';

  return (
    <div
      style={{
        height: h, position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        transformOrigin: 'bottom center',
        transform: falling ? 'rotate(-88deg)' : 'rotate(0deg)',
        opacity: gone ? 0 : 1,
        transition: falling
          ? 'transform 0.52s cubic-bezier(0.25, 0.1, 0.55, 1.0), opacity 0.25s ease 0.38s'
          : 'opacity 0.6s ease',
        animation: stalk.status === 'standing'
          ? `stalk-sway ${stalk.swayDur}s ease-in-out ${stalk.swayDelay}s infinite`
          : 'none',
      }}
    >
      {/* Tassel */}
      <div style={{ position: 'relative', height: Math.round(16 * sc), marginBottom: Math.round(1 * sc) }}>
        {[-5, -2, 0, 2, 5].map((off, k) => (
          <div key={k} style={{
            position: 'absolute', bottom: 0,
            left: `calc(50% + ${off * sc}px)`,
            width: Math.max(1, Math.round(1.5 * sc)),
            height: Math.round((14 - Math.abs(off)) * sc),
            background: `hsl(${stalk.leafHue + 10}, 65%, 60%)`,
            transform: `rotate(${off * 9}deg)`, transformOrigin: 'bottom center',
            borderRadius: 1,
          }} />
        ))}
      </div>

      {/* Corn ear */}
      <div style={{
        position: 'absolute', top: `${Math.round(h * 0.30)}px`, left: '50%',
        marginLeft: Math.round(sw / 2 + 1),
        width: Math.round(10 * sc), height: Math.round(24 * sc),
        zIndex: 2,
      }}>
        {/* Husk outer leaves */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(160deg, hsl(${stalk.leafHue},${stalk.leafSat}%,35%), ${lc})`,
          borderRadius: `${Math.round(4 * sc)}px ${Math.round(5 * sc)}px ${Math.round(4 * sc)}px ${Math.round(3 * sc)}px`,
        }} />
        {/* Kernel rows (yellow peeking out) */}
        <div style={{
          position: 'absolute', top: '20%', bottom: '20%', left: '15%', right: '15%',
          background: 'linear-gradient(180deg, #fef08a, #fbbf24, #d97706)',
          borderRadius: Math.round(2 * sc),
        }} />
        {/* Husk tip */}
        <div style={{
          position: 'absolute', bottom: `-${Math.round(5 * sc)}px`, left: '-30%', right: '-30%',
          height: Math.round(8 * sc),
          background: `linear-gradient(180deg, ${lc}, transparent)`,
          clipPath: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)',
        }} />
        {/* Silk */}
        <div style={{
          position: 'absolute', top: `-${Math.round(6 * sc)}px`, left: '30%',
          width: Math.round(3 * sc), height: Math.round(8 * sc),
          background: `hsl(${stalk.leafHue + 15}, 60%, 68%)`,
          borderRadius: Math.round(2 * sc),
          transform: 'rotate(8deg)',
        }} />
      </div>

      {/* Leaves (3 pairs alternating) */}
      {[
        { pos: 0.42, side: 'left',  w: 24, h: 7, angle: -14 },
        { pos: 0.56, side: 'right', w: 20, h: 6, angle:  14 },
        { pos: 0.70, side: 'left',  w: 17, h: 5, angle: -10 },
        { pos: 0.82, side: 'right', w: 14, h: 4, angle:  10 },
      ].map((lf, k) => {
        const lw = Math.round(lf.w * sc);
        const lh = Math.round(lf.h * sc);
        const radius = lh * 2;
        return (
          <div key={k} style={{
            position: 'absolute',
            top: `${Math.round(h * lf.pos)}px`,
            [lf.side]: '50%',
            width: lw, height: lh,
            background: `linear-gradient(to ${lf.side === 'left' ? 'right' : 'left'}, ${lc}, ${lc2})`,
            borderRadius: lf.side === 'left'
              ? `0 ${radius}px ${radius}px 0`
              : `${radius}px 0 0 ${radius}px`,
            transform: `rotate(${lf.angle}deg)`,
            opacity: 0.88,
          }} />
        );
      })}

      {/* Stem */}
      <div style={{
        width: sw, flex: 1,
        background: `linear-gradient(to bottom, ${lc}, ${lc2})`,
        borderRadius: sw,
      }} />
    </div>
  );
};

// ─── Main container ────────────────────────────────────────────────────────
const STORAGE_KEY = 'corn_harvest_total';

const CombineAnimation = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [combineX, setCombineX] = useState(60);
  const [isActive, setIsActive] = useState(false);
  const targetXRef = useRef(60);
  const rafRef = useRef<number>(0);

  const stalkCount = isMobile ? STALK_MB : STALK_DT;

  const [stalks, setStalks] = useState<Stalk[]>(() =>
    Array.from({ length: STALK_DT }, (_, i) => makeStalk(i, STALK_DT))
  );

  // Persistent total counter (does NOT reset)
  const [totalHarvested, setTotalHarvested] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) || 0 : 0;
  });

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Re-init stalks on mobile toggle
  useEffect(() => {
    setStalks(Array.from({ length: stalkCount }, (_, i) => makeStalk(i, stalkCount)));
  }, [stalkCount]);

  // ── Harvest detection ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const cw = containerRef.current.offsetWidth;
    const hdrW = Math.round(HEADER_W * (isMobile ? SC_MB : SC_DT));
    const pad = isMobile ? 6 : 16;
    const usable = cw - pad * 2;
    const spacing = stalkCount > 1 ? usable / (stalkCount - 1) : usable;

    // Find stalks overlapping with header zone
    const toHarvest: number[] = [];
    stalks.forEach((stalk, i) => {
      if (stalk.status !== 'standing') return;
      const sx = pad + i * spacing;
      if (sx >= combineX && sx <= combineX + hdrW) {
        toHarvest.push(stalk.id);
      }
    });

    if (toHarvest.length === 0) return;

    setStalks(prev =>
      prev.map(s => toHarvest.includes(s.id) ? { ...s, status: 'falling' as StalkStatus } : s)
    );

    setTimeout(() => {
      setStalks(prev =>
        prev.map(s => toHarvest.includes(s.id) && s.status === 'falling'
          ? { ...s, status: 'fallen' as StalkStatus } : s
        )
      );
    }, 620);
  }, [combineX, isMobile, stalkCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto reset: 2s after the last stalk state change → respawn the whole field
  const prevFallenRef = useRef(0);
  useEffect(() => {
    const fallenNow = stalks.filter(s => s.status !== 'standing').length;
    const newlyFallen = Math.max(0, fallenNow - prevFallenRef.current);

    // Update persistent counter with newly harvested stalks
    if (newlyFallen > 0) {
      setTotalHarvested(prev => {
        const next = prev + newlyFallen;
        localStorage.setItem(STORAGE_KEY, String(next));
        return next;
      });
    }
    prevFallenRef.current = fallenNow;

    const anyFallen = fallenNow > 0;
    if (!anyFallen) return; // field is fresh — nothing to reset

    const timer = setTimeout(() => {
      setStalks(Array.from({ length: stalkCount }, (_, i) => makeStalk(i, stalkCount)));
    }, 2000);

    // If stalks change again before 2s (next harvest) — cancel and restart
    return () => clearTimeout(timer);
  }, [stalks, stalkCount]);

  // ── Input handling ─────────────────────────────────────────────────────
  const calcX = useCallback((clientX: number) => {
    if (!containerRef.current) return 0;
    const rect  = containerRef.current.getBoundingClientRect();
    const sc    = isMobile ? SC_MB : SC_DT;
    const hdrW  = Math.round(HEADER_W * sc);
    const pad   = isMobile ? 6 : 16;
    // Allow combine body to slide off-screen right so the header (left side)
    // can reach every stalk including the rightmost ones
    const maxX  = rect.width - pad - hdrW;
    return Math.max(0, Math.min(clientX - rect.left, maxX));
  }, [isMobile]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove  = (e: MouseEvent) => { targetXRef.current = calcX(e.clientX); };
    const onTouch = (e: TouchEvent) => { e.preventDefault(); targetXRef.current = calcX(e.touches[0].clientX); };
    const onIn    = () => setIsActive(true);
    const onOut   = () => setIsActive(false);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('touchmove', onTouch, { passive: false });
    el.addEventListener('mouseenter', onIn);
    el.addEventListener('mouseleave', onOut);
    el.addEventListener('touchstart', onIn, { passive: true });
    el.addEventListener('touchend', onOut);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchmove', onTouch);
      el.removeEventListener('mouseenter', onIn);
      el.removeEventListener('mouseleave', onOut);
      el.removeEventListener('touchstart', onIn);
      el.removeEventListener('touchend', onOut);
    };
  }, [calcX]);

  // Smooth lerp loop
  useEffect(() => {
    const tick = () => {
      setCombineX(prev => {
        const d = targetXRef.current - prev;
        return Math.abs(d) < 0.4 ? targetXRef.current : prev + d * 0.075;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const sc        = isMobile ? SC_MB    : SC_DT;
  const stalkSc   = isMobile ? SC_STK_MB : SC_STK_DT;
  const fieldH    = isMobile ? 80 : 120;
  const stalkBot  = isMobile ? 10 : 16;
  const combBot   = isMobile ? 4  : 6;
  const combineW  = Math.round(COMBINE_W * sc);
  const shadowW   = Math.round(combineW * 0.88);

  // Counter derived values (current field progress)
  const harvestedCount = stalks.filter(s => s.status !== 'standing').length;
  const pct            = stalkCount > 0 ? Math.round((harvestedCount / stalkCount) * 100) : 0;
  const allDone        = harvestedCount === stalkCount && stalkCount > 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden select-none"
      style={{ height: fieldH, cursor: 'crosshair' }}
      aria-hidden="true"
    >
      {/* Ground strip */}
      <div className="absolute bottom-0 left-0 right-0" style={{
        height: isMobile ? 14 : 20,
        background: 'linear-gradient(to top, #060e06, #0a1a0a, #0c200c)',
        borderTop: '1px solid rgba(34,197,94,0.09)',
      }} />

      {/* Soil furrow texture */}
      {Array.from({ length: 5 }, (_, k) => (
        <div key={k} className="absolute" style={{
          bottom: isMobile ? 3 : 6,
          left: `${k * 20 + 2}%`, width: '17%', height: 2,
          background: 'rgba(34,197,94,0.04)', borderRadius: 1,
        }} />
      ))}

      {/* ── Harvest counter ── */}
      <div className="absolute left-1/2 -translate-x-1/2 z-20 pointer-events-none flex flex-col items-center"
        style={{ top: isMobile ? 5 : 7 }}>

        {/* Pill badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 7,
          background: 'rgba(6,14,6,0.72)',
          border: `1px solid ${allDone ? 'rgba(34,197,94,0.55)' : 'rgba(34,197,94,0.2)'}`,
          borderRadius: 999,
          padding: isMobile ? '2px 8px' : '3px 12px',
          backdropFilter: 'blur(6px)',
          transition: 'border-color 0.4s',
        }}>
          {/* Corn icon */}
          <span style={{ fontSize: isMobile ? 11 : 13, lineHeight: 1 }}>🌽</span>

          {/* Progress bar */}
          <div style={{
            width: isMobile ? 52 : 80, height: isMobile ? 3 : 4,
            borderRadius: 999, background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: 999,
              background: allDone
                ? 'linear-gradient(to right, #4ade80, #22c55e)'
                : 'linear-gradient(to right, #22c55e, #86efac)',
              transition: 'width 0.35s ease',
              boxShadow: allDone ? '0 0 6px rgba(34,197,94,0.7)' : 'none',
            }} />
          </div>

          {/* Count text — total harvested */}
          <span style={{
            fontSize: isMobile ? 9 : 11,
            color: allDone ? '#4ade80' : 'rgba(255,255,255,0.45)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.02em',
            transition: 'color 0.3s',
            minWidth: isMobile ? 26 : 36,
            textAlign: 'right',
          }}>
            {totalHarvested.toLocaleString()}
          </span>
        </div>

        {/* "Поле очищено!" flash */}
        {allDone && (
          <div style={{
            marginTop: 3,
            fontSize: isMobile ? 9 : 10,
            color: '#4ade80',
            letterSpacing: '0.05em',
            opacity: 0.85,
          }}>
            поле убрано!
          </div>
        )}
      </div>

      {/* Corn stalks */}
      <div
        className="absolute left-0 right-0 flex items-end justify-around"
        style={{ bottom: stalkBot, paddingLeft: isMobile ? 4 : 16, paddingRight: isMobile ? 4 : 16 }}
      >
        {stalks.map(stalk => (
          <CornStalk key={stalk.id} stalk={stalk} sc={stalkSc} />
        ))}
      </div>

      {/* Combine */}
      <div className="absolute" style={{ bottom: combBot, left: combineX }}>
        <Combine sc={sc} moving={isActive} />
      </div>

      {/* Ground shadow */}
      <div className="absolute pointer-events-none" style={{
        bottom: combBot - 4,
        left: combineX + Math.round(combineW * 0.05),
        width: shadowW,
        height: isMobile ? 3 : 5,
        background: 'radial-gradient(ellipse, rgba(0,0,0,0.5), transparent)',
        filter: `blur(${isMobile ? 1 : 2}px)`,
      }} />

      {/* Hint — desktop only (mobile has counter) */}
      <div className="absolute top-2 right-3 text-white/20 pointer-events-none hidden md:block"
        style={{ fontSize: 11 }}>
        {isActive ? 'косим кукурузу!' : 'наведите мышь → управляйте комбайном'}
      </div>
    </div>
  );
};

export default CombineAnimation;